/**
 * `<ResolutionPanel>` — the ~13 seconds after expiry (`components.md` §2.8).
 *
 * **This is a headline moment, not a loading state.** The settlement price is
 * being decided in front of the user, the outcome is genuinely undetermined for
 * the whole of it and can flip. Thirteen seconds of real suspense, sixty times
 * an hour, is the most emotionally dense surface in the product; rendering it as
 * a spinner would be throwing it away.
 *
 * **It does not occupy the ticket column** (register §F24). It used to, because
 * for thirteen seconds there was nothing to trade and the column was free. The
 * successor now opens in the instant this market expires, so the ticket is live
 * and this renders in a secondary surface beside it. The tension that creates is
 * the whole design problem of the surface: the resolution is the more
 * *interesting* of the two things on screen and the less *important*, and every
 * decision here resolves in favour of the tradable one. If this panel ever pulls
 * focus, steals width or animates across the ticket, it is wrong.
 *
 * ## Three rules it would be easy to break
 *
 * **Nothing renders as final before the API says `RESOLVED`.** The pill reads
 * `LEANING ▲ UP` — never `WON`, never `UP`, never a checkmark. A UI that shows a
 * win and then takes it back is worse than one that shows nothing.
 *
 * **Flash on change of the rendered value, never on receipt** (invariant 5). The
 * headline is a number that moves for ten seconds and then stops; it is the best
 * demonstration of the rule in the product and the worst place to get it wrong.
 * `<NumCell>` owns the comparison and it compares the *string on screen*, so a
 * quiet second that re-renders the same digits does not strobe.
 *
 * **The last ~3s is not a stall.** The price window closes at `+10s` and the
 * market resolves about three seconds later; the label reads `confirming` and
 * must not be styled as an anomaly.
 *
 * ## What it subscribes, and why here rather than in the route
 *
 * `market.{id}` for the state machine, `index.{symbol}` for the running average,
 * and `positions` for the "if it holds" line. All three are subscribed *by this
 * component* — invariant 2: `useStore` is `useSyncExternalStore`, so hoisting the
 * index subscription into `<TradeRoute>` would re-render the chart, the ladder
 * and the countdown five times a second for the benefit of one panel.
 */

import { memo, useEffect, useState } from 'react'
import { Glyph, NumCell, Pill } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { useIndex, useMarket, usePositions } from '@/app/use-runtime'
import { formatPriceString, formatShares, formatUsdc } from '@/lib/format'
import { formatFixed, parseFixed, payoutMicroUsdc, SHARES_DP } from '@/lib/decimal'
import { observationFrom, toNumber, type Observation } from '@/domain/price'
import { SettlementPrint, leaningOutcome, observationSentence, resolutionView } from '@/domain/resolution'
import type { MarketState } from '@/domain/lifecycle'

/** The states this surface is *about*. `EXPIRED` is sub-second and is here so
 *  the panel never renders a gap between the last tick and `RESOLVING`. */
const RENDERED_STATES: ReadonlySet<MarketState> = new Set<MarketState>([
  'EXPIRED',
  'RESOLVING',
  'RESOLVING_DEGRADED',
  'RESOLVED',
  'VOIDED',
])

/**
 * And the two it holds only until `resolve_eta_ms` — the market's expiry plus
 * `/config`'s `resolve_eta_offset_ms`.
 *
 * A market resolves the moment its print lands (NET-SETTLEMENT-PLAN.md A1), and
 * where nothing holds `SETTLED` back — any venue with no chain behind it, and an
 * untraded market on one that has — `RESOLVED` and `SETTLED` land together and
 * the gateway coalesces them into one frame. So a panel that left at `SETTLED`
 * was on screen for no time at all, and a user with no position in the market
 * never saw how it ended (2026-09-30). It stays until the latest instant a
 * result is published for, whatever the state.
 */
const HELD_STATES: ReadonlySet<MarketState> = new Set<MarketState>(['SETTLED', 'REFUNDED'])

export interface ResolutionPanelProps {
  marketId: string
  /** The asset's index channel — the feed the settlement print is read from. */
  symbol: string
  /** Corrected server time. Never `Date.now()` (invariant 7). */
  nowMs: number
  /** The socket is not `OPEN`. A confidently wrong settlement price is the worst
   *  pixel in the product, so the panel desaturates and says so rather than
   *  freezing on a stale number as though it were live. */
  stale?: boolean
  /**
   * One line rather than the card — below 768px, where the card has nowhere to
   * go but on top of the book for the first seconds of every minute. Same
   * numbers from the same derivation; only the rendering is smaller.
   */
  compact?: boolean
  /** The compact line carries its own dismiss; the card's is its surface's. */
  onDismiss?: () => void
  className?: string
}

export const ResolutionPanel = memo(function ResolutionPanel({
  marketId,
  symbol,
  nowMs,
  stale,
  compact = false,
  onDismiss,
  className,
}: ResolutionPanelProps) {
  const market = useMarket(marketId)
  const index = useIndex(symbol)
  const { rows: positions } = usePositions<Record<string, unknown>>()

  const state = (market?.state as MarketState | undefined) ?? 'RESOLVING'
  const expiryAtMs = numberOf(market, 'expiry_at_ms')
  const settlementStartMs = numberOf(market, 'settlement_window_start_ms') ?? expiryAtMs
  const settlementEndMs = numberOf(market, 'settlement_window_end_ms')
  const resolveEtaMs = numberOf(market, 'resolve_eta_ms')
  const resolution = (market?.resolution ?? null) as Record<string, unknown> | null

  /**
   * The settlement print as the client can see it, rebuilt when the market
   * moves: the index tick stamped at the settlement instant once it arrives,
   * and the newest tick before it until then (`SettlementPrint`, §F74).
   *
   * The tracker *and* its value are state, together in one object, and the
   * pairing is deliberate: the tracker mutates, so React has nothing to
   * compare, and reading it during render would leave the panel showing the
   * value from the previous frame — the exact "paints a number one tick behind"
   * shape `<StrikeMeter>`'s header warns about.
   *
   * Reset on a market change, in render, which is React's own answer for state
   * derived from props: the alternative is an effect that renders one frame of
   * the *previous* market's settlement price under this market's id.
   */
  const printAtMs = settlementEndMs ?? expiryAtMs
  const printKey = `${marketId}:${printAtMs}`
  const [print, setPrint] = useState<PrintState>(() => freshPrint(printKey, printAtMs))
  if (print.key !== printKey) setPrint(freshPrint(printKey, printAtMs))

  useEffect(() => {
    // `observed_at_ms` off the payload, not the arrival time: a tick is stamped
    // by the oracle and the print is defined in the oracle's terms. A frame
    // that queued behind the boundary burst would otherwise be attributed to the
    // millisecond it was drained rather than the one it describes.
    const observedAtMs = numberOf(index, 'observed_at_ms') ?? nowMs
    if (!print.acc.push(index, observedAtMs)) return
    setPrint((held) => (held.acc === print.acc ? { ...held, value: held.acc.value() } : held))
  }, [index, nowMs, print])

  const view = resolutionView({
    state,
    expiryAtMs: expiryAtMs ?? 0,
    settlementStartMs: settlementStartMs ?? 0,
    resolveEtaMs,
    nowMs,
  })

  // The server's number the instant it exists, the client's estimate until then.
  // §2.8: the panel must settle *onto* the final value rather than treating its
  // own last frame as final.
  const final = observationFrom(resolution?.settlement_price)
  const settlement = final ?? print.value
  const strike = observationFrom(market?.strike) ?? observationFrom(resolution?.strike_price)
  const outcome =
    (resolution?.outcome === 'UP' || resolution?.outcome === 'DOWN' ? resolution.outcome : null) ??
    leaningOutcome(settlement, strike)

  const delta = deltaOf(settlement, strike)
  const priceDecimals = 2
  const position = positions.find((row) => row.market_id === marketId) ?? null
  const held = outcome === null ? null : sharesOf(position, outcome)

  const tone = view.phase === 'VOID' ? 'down' : view.degraded ? 'warning' : outcome === 'DOWN' ? 'down' : 'up'

  // `SETTLED`/`REFUNDED` hold the panel until `resolve_eta_ms` (`HELD_STATES`),
  // with the final result on it; a position's receipt renders above it in the
  // same surface for its second (§2.6), the newer statement of the same market.
  // `CANCELLED` never had a position or a settlement window, so there is nothing
  // to resolve; the rail marks it and the page fast-forwards.
  const holding = HELD_STATES.has(state) && resolveEtaMs !== null && nowMs < resolveEtaMs
  if (!RENDERED_STATES.has(state) && !holding) return null

  if (compact) {
    // The phase pill (with its bar), the direction, and how far from the
    // strike. The rules above hold unchanged: `LEANING` until the API says
    // `RESOLVED`, and the delta is muted rather than coloured so the only
    // directional colour on the line is the one carrying a glyph and a word.
    return (
      <div
        data-testid="resolution-panel"
        data-compact="true"
        data-market={marketId}
        data-phase={view.phase}
        data-stale={stale ? 'true' : undefined}
        className={cx(
          'flex min-h-[var(--ud-tap-min)] items-center gap-2 pl-3',
          'data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
          onDismiss ? 'pr-0' : 'pr-3',
          className,
        )}
      >
        <Pill
          tone={view.degraded ? 'warning' : view.phase === 'FINAL' || view.phase === 'VOID' ? tone : 'info'}
          {...(view.phase === 'FINAL' || view.phase === 'VOID' ? {} : { progress: view.progress })}
          {...(stale ? { muted: true } : {})}
        >
          {headline(view.phase, view.degraded)}
        </Pill>
        <span className="ud-num flex min-w-0 items-baseline gap-1_5 text-body-sm">
          {outcome !== null && view.phase !== 'VOID' ? (
            <span
              className={cx(
                'shrink-0 text-caption font-medium uppercase',
                outcome === 'DOWN' ? 'text-down-text' : 'text-up-text',
              )}
            >
              {view.phase === 'FINAL' ? null : 'LEANING '}
              <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} /> {outcome}
            </span>
          ) : null}
          {delta === null ? null : (
            <span className="truncate text-text-secondary">{signedFixed(delta, priceDecimals)}</span>
          )}
        </span>
        {onDismiss ? (
          <button
            type="button"
            aria-label="dismiss resolution"
            onClick={onDismiss}
            className="ml-auto inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] shrink-0 items-center justify-center rounded-sm text-body-sm text-text-tertiary hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
          >
            ×
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      data-testid="resolution-panel"
      data-market={marketId}
      data-phase={view.phase}
      data-stale={stale ? 'true' : undefined}
      className={cx(
        'flex flex-col gap-2 p-3 data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Pill
          tone={view.degraded ? 'warning' : view.phase === 'FINAL' || view.phase === 'VOID' ? tone : 'info'}
          // Omitted, not `null`: a resolved market has no bar at all, where
          // `null` is the *indeterminate* bar a late one gets.
          {...(view.phase === 'FINAL' || view.phase === 'VOID' ? {} : { progress: view.progress })}
          {...(stale ? { muted: true } : {})}
        >
          {headline(view.phase, view.degraded)}
        </Pill>
        <span className="ud-num truncate text-micro text-text-tertiary" title={marketId}>
          {marketId}
        </span>
      </div>

      {/* The number being decided. Reserved to the width of the widest plausible
          value so the digits never reflow as they land. */}
      <div className="flex flex-col gap-0_5">
        <span className="text-micro text-text-tertiary">Settlement price</span>
        <NumCell
          align="left"
          reserve={12}
          // `display-sm` (24px), not §2.8's `display` (32px). The sketch there
          // was drawn while this panel owned the ticket column; it is now a
          // secondary surface beside a live ticket, and the same paragraph that
          // moved it says the resolution must never steal focus from the
          // tradable market. The type scale is part of that (register §F25).
          className="text-display-sm text-text-primary"
          value={settlement ? formatObservation(settlement, priceDecimals) : '—'}
          {...compareProp(settlement)}
        />
        <span
          className={cx(
            'ud-num text-body-sm',
            delta === null ? 'text-text-tertiary'
            : delta >= 0 ? 'text-up-text'
            : 'text-down-text',
          )}
        >
          {delta === null ? (
            '—'
          ) : (
            <>
              <Glyph name={delta >= 0 ? 'up' : 'down'} /> {signedFixed(delta, priceDecimals)}{' '}
              <span className="text-text-tertiary">vs strike</span>
            </>
          )}
        </span>
        {/* The instant this settles on — the print stamped at expiry, which is
            also the next market's strike. Named here, under the number it
            describes, and not under the strike row, where it read as the strike's
            own time (§F74). */}
        <span data-testid="settlement-print-at" className="text-caption text-text-tertiary">
          {observationSentence(printAtMs)}
        </span>
      </div>

      <div className="flex items-baseline justify-between gap-2 border-t border-border-subtle pt-1_5">
        <span className="text-micro text-text-tertiary">Strike</span>
        <span className="ud-num text-body-sm text-text-secondary">
          {strike ? formatObservation(strike, priceDecimals) : '—'}
        </span>
      </div>


      <div className="flex flex-col gap-1 border-t border-border-subtle pt-2">
        <span className="flex items-center gap-1_5">
          <Pill tone={tone}>
            {outcome === null ? (
              statusWord(view.phase)
            ) : (
              <>
                {view.phase === 'FINAL' ? 'RESOLVED' : view.phase === 'VOID' ? 'VOIDED' : 'LEANING'}{' '}
                <Glyph name={outcome === 'UP' ? 'up' : 'down'} label={null} /> {outcome}
              </>
            )}
          </Pill>
          {stale ? <Pill tone="warning">STALE</Pill> : null}
        </span>
        <span className="text-body-sm text-text-secondary">{secondary(view.phase, view.degraded, held, outcome)}</span>
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------

interface PrintState {
  key: string
  acc: SettlementPrint
  value: Observation | null
}

function freshPrint(key: string, atMs: number | null): PrintState {
  return { key, acc: new SettlementPrint(atMs ?? 0), value: null }
}

// ---------------------------------------------------------------------------
// Copy — docs/07 §4, verbatim. The most-seen strings in the product.
// ---------------------------------------------------------------------------

export function headline(phase: ReturnType<typeof resolutionView>['phase'], degraded: boolean): string {
  if (degraded) return 'RESOLVING · DEGRADED'
  switch (phase) {
    case 'FINAL':
      return 'RESOLVED'
    case 'VOID':
      return 'VOIDED'
    case 'LATE':
      return 'RESOLVING'
    default:
      return 'RESOLVING'
  }
}

function statusWord(phase: ReturnType<typeof resolutionView>['phase']): string {
  return phase === 'VOID' ? 'VOIDED' : phase === 'FINAL' ? 'RESOLVED' : 'RESOLVING'
}

/**
 * The line under the pill.
 *
 * Every string here counts *toward* something. `Loading`, `Please wait`,
 * `Waiting for…`, `No data` and `Empty` are banned across this surface
 * (docs/07 §4) — nothing is loading, a market is being decided.
 *
 * `confirming` covers `[window close, resolve_eta]`. `docs/03` §6.2's frame
 * table shows a second word, `computing`, at `T+72`; it is in neither
 * `docs/07` §4's table nor `components.md` §2.8, which both say `confirming`
 * for the whole of it, and two documents naming one string beats one naming a
 * second (register §F25).
 */
export function secondary(
  phase: ReturnType<typeof resolutionView>['phase'],
  degraded: boolean,
  held: bigint | null,
  outcome: 'UP' | 'DOWN' | null,
): string {
  if (phase === 'VOID') return 'voided — every order refunded at entry cost'
  if (degraded) return 'degraded — this market will still settle'
  if (phase === 'LATE') return 'taking longer than usual — 20s timeout'
  if (held === null || held <= 0n || outcome === null) {
    // The successor is already trading, so there is nothing to count toward —
    // which is why this is three words and not the sentence it used to be.
    return phase === 'CONFIRMING' ? 'confirming · no position' : 'no position'
  }
  const payout = formatUsdc(formatFixed(payoutMicroUsdc(held), 6))
  const line = `${formatShares(formatFixed(held, SHARES_DP))} ${outcome} → ${payout}`
  return phase === 'FINAL' ? `${line} paid` : phase === 'CONFIRMING' ? `confirming · ${line} if it holds` : `${line} if it holds`
}

// ---------------------------------------------------------------------------

function numberOf(source: unknown, field: string): number | null {
  const value = (source as Record<string, unknown> | null)?.[field]
  return typeof value === 'number' ? value : null
}

/** Shares of one outcome held in this market. `up_shares`/`down_shares` are the
 *  authoritative fields; `net_shares` is "never the settlement input". */
function sharesOf(position: Record<string, unknown> | null, outcome: 'UP' | 'DOWN'): bigint | null {
  const raw = position?.[outcome === 'UP' ? 'up_shares' : 'down_shares']
  if (typeof raw !== 'string') return null
  try {
    return parseFixed(raw, SHARES_DP)
  } catch {
    return null
  }
}

/**
 * The flash's ordering key: the observation's own mantissa, as a `bigint`.
 *
 * Never a float and never the rendered string — invariant 5 says the flash
 * *triggers* on the rendered value changing and invariant 6 says its
 * *direction* comes from an exact comparison, and those are two different
 * inputs. At `expo = −18` the mantissa is 22 digits, so a `Number` here would
 * make two genuinely different settlement prices compare equal.
 */
function compareProp(observation: Observation | null): { compare?: bigint } {
  if (!observation) return {}
  try {
    return { compare: BigInt(observation.raw) }
  } catch {
    return {}
  }
}

/** For the delta line only — a difference of two prices, in pixels' worth of
 *  precision. The outcome itself is decided on `raw` (`leaningOutcome`). */
function deltaOf(settlement: Observation | null, strike: Observation | null): number | null {
  const a = toNumber(settlement)
  const b = toNumber(strike)
  return a === null || b === null ? null : a - b
}

function formatObservation(observation: Observation, dp: number): string {
  return formatPriceString(observation.value, dp)
}

function signedFixed(value: number, dp: number): string {
  return `${value < 0 ? '-' : '+'}$${Math.abs(value).toFixed(dp)}`
}
